import React, { useEffect, useState } from "react";
import {
  FaCheckCircle,
  FaCloudUploadAlt,
  FaImage,
  FaNewspaper,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { toast } from "react-toastify";

import AdminDrawer from "./AdminDrawer";

import { uploadImage } from "../../../../../services/newsServices";

import {
  emptyNewsForm,
  getAuthorId,
  mapArticleToForm,
  resolveMediaUrl,
} from "../utils/actualitesUtils";

export default function NewsFormDrawer({
  isOpen,
  mode = "create",
  article,
  sectionId,
  saving,
  onClose,
  onCreate,
  onUpdate,
}) {
  const isEdit = mode === "edit";

  const [formData, setFormData] = useState(emptyNewsForm);
  const [imageFile, setImageFile] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && article) {
      setFormData(mapArticleToForm(article));
    } else {
      setFormData(emptyNewsForm);
    }

    setImageFile(null);
    setLocalPreview("");
    setUploading(false);
  }, [isOpen, isEdit, article]);

  useEffect(() => {
    if (!imageFile) return undefined;

    const url = URL.createObjectURL(imageFile);
    setLocalPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const title = isEdit ? "Modifier une actualité" : "Créer une actualité";

  const subtitle = isEdit
    ? "Mettez à jour le titre, le contenu, l’image et la visibilité."
    : "Ajoutez une nouvelle actualité pour l’association active.";

  const preview =
    localPreview ||
    resolveMediaUrl(formData.image_url, "/images/asso_placeholder.webp");

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      content: value,
    }));
  };

  const uploadPhotoIfNeeded = async () => {
    if (!imageFile) return formData.image_url;

    try {
      setUploading(true);

      const imageData = new FormData();
      imageData.append("image", imageFile);
      imageData.append("category", "news");

      const response = await uploadImage(imageData);

      return (
        response?.filePath ||
        response?.url ||
        response?.path ||
        response?.image_url ||
        response?.data?.filePath ||
        response?.data?.url ||
        ""
      );
    } catch (error) {
      console.error("Erreur upload image actualité :", error);
      toast.error("Erreur lors de l'upload de l'image.");
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!sectionId) {
      toast.error("Aucune association sélectionnée.");
      return;
    }

    if (!formData.title.trim()) {
      toast.error("Le titre de l'actualité est obligatoire.");
      return;
    }

    try {
      const finalImageUrl = await uploadPhotoIfNeeded();

      const payload = {
        title: formData.title,
        content: formData.content,
        image_url: finalImageUrl,
        is_active: Boolean(formData.is_active),
        section_id: Number(sectionId),
      };

      if (isEdit) {
        await onUpdate?.(article.id, payload);
      } else {
        await onCreate?.({
          ...payload,
          author_id: getAuthorId(),
        });
      }
    } catch {
      // Toast déjà géré dans uploadPhotoIfNeeded ou dans le hook.
    }
  };

  return (
    <AdminDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width="max-w-5xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <FormBlock
            icon={<FaNewspaper />}
            title="Contenu de l'actualité"
            subtitle="Titre, texte et statut de publication."
          >
            <div className="grid gap-4">
              <InputField
                label="Titre de l'actualité"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />

              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Contenu
                </label>

                <div className="rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
                  <ReactQuill
                    value={formData.content}
                    onChange={handleContentChange}
                    theme="snow"
                    className="min-h-[220px] text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-white/10">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={Boolean(formData.is_active)}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />

                <span className="inline-flex items-center gap-2">
                  <FaCheckCircle
                    className={
                      formData.is_active ? "text-emerald-500" : "text-slate-400"
                    }
                  />
                  Publier l'actualité sur le site
                </span>
              </label>
            </div>
          </FormBlock>

          <FormBlock
            icon={<FaImage />}
            title="Image de l'actualité"
            subtitle="Ajoutez une URL ou téléversez une image depuis votre ordinateur."
          >
            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
              <div className="space-y-4">
                <InputField
                  label="URL de l'image"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  placeholder="https://... ou /uploads/..."
                />

                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-6 text-sm font-bold text-slate-600 transition hover:border-primary hover:text-primary dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
                  <FaCloudUploadAlt />
                  {imageFile ? imageFile.name : "Choisir un fichier image"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      setImageFile(event.target.files?.[0] || null)
                    }
                    className="hidden"
                  />
                </label>

                <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
                  Conseil : utilisez une image horizontale pour un meilleur
                  rendu sur les cartes du site public.
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950">
                <p className="mb-3 text-sm font-bold text-slate-950 dark:text-white">
                  Aperçu
                </p>

                <div className="aspect-video overflow-hidden rounded-[20px] bg-slate-100 dark:bg-slate-900">
                  <img
                    src={preview}
                    alt="Aperçu actualité"
                    className="h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.src = "/images/asso_placeholder.webp";
                    }}
                  />
                </div>
              </div>
            </div>
          </FormBlock>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving || uploading}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving || uploading}
            className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving || uploading
              ? "Enregistrement..."
              : isEdit
                ? "Mettre à jour"
                : "Créer"}
          </button>
        </div>
      </form>
    </AdminDrawer>
  );
}

function FormBlock({ icon, title, subtitle, children }) {
  return (
    <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60">
      <div className="mb-5 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {icon}
        </span>

        <div>
          <h3 className="font-bold text-slate-950 dark:text-white">{title}</h3>

          {subtitle && (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

function InputField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}